import React from 'react'

import { buildComponent } from '../factory'
import { Category } from '../../state/models/audio'
import { useStoreState } from '../../hooks/state'

import * as UI from '@mantine/core'

export const CategorySelector = buildComponent<{
  value?:
    | string
    | {
        _id: string
        short?: { fr: string; en: string }
        long?: { fr: string; en: string }
      }
  audioType: 'theme' | 'book' | 'background'
  onChange: (categoryId: string | undefined) => void
  showDebug?: boolean
}>()
  .withLifecycle(({ props }) => {
    const allCategories = useStoreState((store) => store.audio.categories)

    // Add state to track pending parent selection
    const [pendingParentId, setPendingParentId] = React.useState<string | null>(
      null,
    )

    // Filtrer les catégories par type d'audio
    const relevantCategories = allCategories.filter(
      (cat: Category) => cat.type === props.audioType,
    )

    // Catégories principales (sans parent OU avec un parent qui n'existe pas dans ce type)
    const parentCategories = relevantCategories.filter((cat: Category) => {
      if (!cat.parent) {
        // Vraie catégorie parent (sans parent du tout)
        return true
      }

      // Vérifier si le parent existe dans les catégories du même type
      const parentExistsInSameType = relevantCategories.some(
        (parentCat: Category) => parentCat._id === cat.parent?._id,
      )

      // Si le parent n'existe pas dans le même type, traiter comme catégorie principale
      return !parentExistsInSameType
    })

    // Extraire l'ID de la valeur (string ou objet)
    const currentValueId =
      typeof props.value === 'string' ? props.value : props.value?._id

    // Trouver la catégorie sélectionnée
    const selectedCategory = relevantCategories.find(
      (cat: Category) => cat._id === currentValueId,
    )

    // Déterminer le parent sélectionné
    const selectedParent = (() => {
      // Si on a une sélection en attente, utiliser ça
      if (pendingParentId) {
        return parentCategories.find((cat) => cat._id === pendingParentId)
      }

      // Si la catégorie sélectionnée a un parent, trouver ce parent
      if (selectedCategory?.parent) {
        return relevantCategories.find(
          (cat: Category) => cat._id === selectedCategory.parent?._id,
        )
      }

      // Si la catégorie sélectionnée n'a pas de parent, c'est elle le parent
      if (selectedCategory && !selectedCategory.parent) {
        return selectedCategory
      }

      return null
    })()

    // Sous-catégories de la catégorie parent sélectionnée
    const subCategories = selectedParent
      ? relevantCategories.filter(
          (cat: Category) => cat.parent?._id === selectedParent._id,
        )
      : []

    const handleParentChange = (parentId: string | null) => {
      if (!parentId) {
        props.onChange(undefined)
        setPendingParentId(null)
        return
      }

      const parent = parentCategories.find(
        (cat: Category) => cat._id === parentId,
      )
      if (!parent) {
        return
      }

      // Vérifier s'il y a des sous-catégories pour cette catégorie
      const hasSubCategories = relevantCategories.some(
        (cat: Category) => cat.parent?._id === parentId,
      )

      if (hasSubCategories) {
        // Si il y a des sous-catégories, on ne sélectionne pas encore
        // Mais on garde en mémoire quel parent est sélectionné
        setPendingParentId(parentId)
        props.onChange(undefined)
      } else {
        // Si il n'y a pas de sous-catégories, on peut sélectionner la catégorie parent
        setPendingParentId(null)
        props.onChange(parentId)
      }
    }

    const handleSubCategoryChange = (subCategoryId: string | null) => {
      setPendingParentId(null)
      props.onChange(subCategoryId || undefined)
    }

    // Debug information
    const debugInfo = {
      totalCategories: allCategories.length,
      relevantCategories: relevantCategories.length,
      parentCategories: parentCategories.length,
      subCategories: subCategories.length,
      currentValueId,
      selectedCategoryId: selectedCategory?._id,
      selectedParentId: selectedParent?._id,
      pendingParentId,
      audioType: props.audioType,
      categoriesByType: {
        theme: allCategories.filter((c: Category) => c.type === 'theme').length,
        book: allCategories.filter((c: Category) => c.type === 'book').length,
        background: allCategories.filter(
          (c: Category) => c.type === 'background',
        ).length,
      },
      allCategoryTypes: Array.from(
        new Set(allCategories.map((c: Category) => c.type)),
      ),
      relevantCategoriesDetails: relevantCategories.map((c: Category) => {
        const parentExistsInSameType = c.parent
          ? relevantCategories.some(
              (parentCat: Category) => parentCat._id === c.parent?._id,
            )
          : false
        return {
          id: c._id,
          slug: c.slug,
          type: c.type,
          hasParent: !!c.parent,
          parentId: c.parent?._id,
          shortFr: c.short?.fr,
          shortEn: c.short?.en,
          parentExistsInSameType,
          isOrphaned: !!c.parent && !parentExistsInSameType,
        }
      }),
    }

    return {
      parentCategories,
      subCategories,
      selectedParent,
      selectedCategory,
      handleParentChange,
      handleSubCategoryChange,
      hasSubCategories: subCategories.length > 0,
      debugInfo,
    }
  })
  .withRender(({ props, lifecycle }) => (
    <UI.Stack spacing="md">
      {/* Debug information */}
      {props.showDebug && (
        <UI.Card withBorder p="md" sx={{ backgroundColor: '#f8f9fa' }}>
          <UI.Text weight={700} size="sm" mb="xs">
            🐛 Debug Information
          </UI.Text>
          <UI.Stack spacing="xs">
            <UI.Text size="xs">
              <b>Audio Type:</b> {lifecycle.debugInfo.audioType}
            </UI.Text>
            <UI.Text size="xs">
              <b>Total Categories:</b> {lifecycle.debugInfo.totalCategories}
            </UI.Text>
            <UI.Text size="xs">
              <b>Relevant Categories ({props.audioType}):</b>{' '}
              {lifecycle.debugInfo.relevantCategories}
            </UI.Text>
            <UI.Text size="xs">
              <b>Parent Categories:</b> {lifecycle.debugInfo.parentCategories}
            </UI.Text>
            <UI.Text size="xs">
              <b>Sub Categories:</b> {lifecycle.debugInfo.subCategories}
            </UI.Text>
            <UI.Text size="xs">
              <b>Current Value ID:</b>{' '}
              {lifecycle.debugInfo.currentValueId || 'None'}
            </UI.Text>
            <UI.Text size="xs">
              <b>Selected Category ID:</b>{' '}
              {lifecycle.debugInfo.selectedCategoryId || 'None'}
            </UI.Text>
            <UI.Text size="xs">
              <b>Selected Parent ID:</b>{' '}
              {lifecycle.debugInfo.selectedParentId || 'None'}
            </UI.Text>
            <UI.Text size="xs">
              <b>Pending Parent ID:</b>{' '}
              {lifecycle.debugInfo.pendingParentId || 'None'}
            </UI.Text>
            <UI.Text size="xs">
              <b>Categories by Type:</b> Theme:
              {lifecycle.debugInfo.categoriesByType.theme}, Book:
              {lifecycle.debugInfo.categoriesByType.book}, Background:
              {lifecycle.debugInfo.categoriesByType.background}
            </UI.Text>
            <UI.Text size="xs">
              <b>All Category Types Found:</b>{' '}
              {lifecycle.debugInfo.allCategoryTypes.join(', ')}
            </UI.Text>

            {lifecycle.debugInfo.relevantCategoriesDetails.length > 0 && (
              <UI.Accordion variant="contained">
                <UI.Accordion.Item value="categories-detail">
                  <UI.Accordion.Control>
                    <UI.Text size="xs">
                      View Relevant Categories Details (
                      {lifecycle.debugInfo.relevantCategoriesDetails.length})
                    </UI.Text>
                  </UI.Accordion.Control>
                  <UI.Accordion.Panel>
                    <UI.Stack spacing="xs">
                      {lifecycle.debugInfo.relevantCategoriesDetails.map(
                        (cat: {
                          id: string
                          slug: string
                          type: string
                          hasParent: boolean
                          parentId?: string
                          shortFr?: string
                          shortEn?: string
                          parentExistsInSameType: boolean
                          isOrphaned: boolean
                        }) => (
                          <UI.Group key={cat.id} spacing="xs">
                            <UI.Badge
                              size="xs"
                              color={
                                cat.isOrphaned
                                  ? 'orange'
                                  : cat.hasParent
                                  ? 'blue'
                                  : 'green'
                              }
                              variant="light"
                            >
                              {cat.isOrphaned
                                ? 'Orphaned'
                                : cat.hasParent
                                ? 'Child'
                                : 'Parent'}
                            </UI.Badge>
                            <UI.Text size="xs">
                              <b>{cat.shortFr || cat.shortEn || cat.slug}</b>
                            </UI.Text>
                            <UI.Text size="xs" color="dimmed">
                              ({cat.id.slice(-8)})
                            </UI.Text>
                            {cat.hasParent && (
                              <UI.Text size="xs" color="dimmed">
                                Parent: {cat.parentId?.slice(-8)}
                                {cat.isOrphaned && ' (missing)'}
                              </UI.Text>
                            )}
                          </UI.Group>
                        ),
                      )}
                    </UI.Stack>
                  </UI.Accordion.Panel>
                </UI.Accordion.Item>
              </UI.Accordion>
            )}
          </UI.Stack>
        </UI.Card>
      )}

      <UI.Select
        label={
          <b style={{ fontSize: '2rem' }}>
            {props.audioType === 'theme' && 'Catégorie de thématique'}
            {props.audioType === 'book' && "Catégorie d'audiobook"}
            {props.audioType === 'background' && 'Catégorie de fond sonore'}
          </b>
        }
        description="Sélectionnez la catégorie à laquelle appartient cet audio"
        placeholder="Choisir une catégorie"
        clearable
        value={lifecycle.selectedParent?._id || null}
        onChange={lifecycle.handleParentChange}
        data={lifecycle.parentCategories.map((cat: Category) => ({
          value: cat._id,
          label: cat.short?.fr || cat.short?.en || cat.slug,
        }))}
      />

      {lifecycle.selectedParent && lifecycle.subCategories.length > 0 && (
        <UI.Select
          label={<b style={{ fontSize: '1.5rem' }}>Sous-catégorie</b>}
          description="Cette catégorie a des sous-catégories. Vous devez en choisir une."
          placeholder="Choisir une sous-catégorie"
          required
          value={
            lifecycle.selectedCategory?.parent
              ? lifecycle.selectedCategory._id
              : null
          }
          onChange={lifecycle.handleSubCategoryChange}
          data={lifecycle.subCategories.map((cat: Category) => ({
            value: cat._id,
            label: cat.short?.fr || cat.short?.en || cat.slug,
          }))}
        />
      )}
    </UI.Stack>
  ))
